/**
 * CorporateProfilePage — top-level corporate facts page.
 *
 * Per owner directive (Feb 2, 2026): Corporate Profile is a SEPARATE
 * top-level nav item because banks, vendors, partners, and corporate
 * visitors need official company facts quickly without scrolling
 * through About Us narrative.
 *
 * Source of truth for facts/rows: `/data/corporateProfile.js`
 * (same data used previously inside About > Profile section, which
 * has now been removed per owner directive).
 *
 * Safety: capital appears ONLY on this page (never on Home / About Us).
 */
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";

import Breadcrumb from "@/components/Breadcrumb";
import Reveal from "@/components/motion/Reveal";
import PageSideAura from "@/components/motion/PageSideAura";
import { profileRows, registeredBusinessScope } from "@/data/corporateProfile";
import SEO, { PAGE_SEO } from "@/components/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

const MILESTONES = (isJa) => [
  {
    period: "2013",
    title: isJa
      ? "横須賀にてNoJo Enterprise設立"
      : "NoJo Enterprise established in Yokosuka",
  },
  {
    period: "2014 – 2020",
    title: isJa
      ? "日本国内における実務サービス運営・カスタマーサポートを継続"
      : "Practical service operations and customer support in Japan",
  },
  {
    period: "2021 – 2024",
    title: isJa
      ? "デジタル運営とETC関連サポートを拡大"
      : "Expanded digital operations and ETC-related support",
  },
  {
    period: "2025",
    title: isJa
      ? "コーポレート基盤の強化"
      : "Strengthened corporate foundation",
  },
  {
    period: "2026 – 2027",
    title: isJa
      ? "モビリティ・記録管理・デジタルインフラ領域への展開を継続"
      : "Expanding toward mobility, records, and digital infrastructure",
  },
];

export default function CorporateProfilePage({ lang }) {
  const isJa = lang === "ja";
  const rows = profileRows(lang);

  return (
    <article className="bg-white" data-testid="corporate-profile-page">
      <SEO {...PAGE_SEO.corporateProfile[lang]} path={`/${lang}/corporate-profile`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "会社概要" : "Corporate Profile", url: `/${lang}/corporate-profile` },
        ])}
      />
      <Breadcrumb
        lang={lang}
        crumbs={[{ label: isJa ? "会社概要" : "Corporate Profile" }]}
      />

      {/* Hero (condensed). Owner directive Feb 2026: drop the small
          "Corporate Profile" eyebrow text — the icon stays as a visual
          anchor, and the H1 carries the heading on its own. */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 md:py-6">
          <div className="flex items-baseline gap-3 flex-wrap">
            <Building2 className="h-3.5 w-3.5 text-[#0F1D2D]" strokeWidth={2.25} />
            <h1
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-2xl md:text-[28px] tracking-tight font-bold leading-[1.1]",
              ].join(" ")}
            >
              {isJa ? "会社概要" : "Corporate Profile"}
            </h1>
            <span className="text-[#CBD5E1]">·</span>
            <p className="text-[13px] leading-[1.6] text-slate-600 max-w-2xl">
              {isJa
                ? "NoJo Enterpriseの会社情報、主要拠点、事業領域。"
                : "Official company facts — legal information, location, business areas, operated services."}
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          MIDDLE CONTENT — wrapped together so a single subtle side aura
          sits behind the body sections (Public Facts → Scope → Milestones)
          but NOT behind the hero, the "what's shipped" section, or the
          bottom closing block.
          ═══════════════════════════════════════════════════════════════════ */}
      <div className="relative isolate" data-testid="corporate-profile-middle">
        <PageSideAura testId="corporate-profile-side-aura" />

      {/* Corporate facts table — REUSES the canonical profileRows() data */}
      <section
        id="profile"
        className="bg-[#F8F9FA] border-y border-[#E2E8F0] relative"
        data-testid="corporate-profile-table-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-6">
            <h2
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-2xl md:text-3xl tracking-tight font-bold",
              ].join(" ")}
            >
              {isJa ? "公開会社情報" : "Public corporate facts"}
            </h2>
            <div
              className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] font-mono-display uppercase font-semibold text-slate-500"
              data-testid="corporate-profile-certified-stamp"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-slate-600" strokeWidth={2.25} />
              <span>
                {isJa
                  ? "登記情報 取得日 2026-05-14"
                  : "Certified as of 2026-05-14"}
              </span>
            </div>
          </div>

          <dl
            className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden shadow-[0_18px_40px_-26px_rgba(15,29,45,0.18)]"
            data-testid="corporate-facts-table"
          >
            {rows.map((row, i) => (
              <div
                key={row.key}
                data-testid={`corporate-profile-row-${row.key}`}
                className={[
                  "grid grid-cols-1 md:grid-cols-12 gap-y-1 md:gap-y-0 px-5 md:px-7 py-4 md:py-5",
                  i % 2 === 1 ? "bg-[#F8F9FA]" : "bg-white",
                  i !== rows.length - 1 ? "border-b border-[#EEF2F6]" : "",
                ].join(" ")}
              >
                <dt
                  className={[
                    "md:col-span-4 lg:col-span-3",
                    isJa ? "font-ja" : "font-display",
                    "text-[12.5px] md:text-[13px] tracking-tight text-[#475569] font-semibold mb-1 md:mb-0 md:pr-4 md:border-r md:border-[#E2E8F0]",
                  ].join(" ")}
                >
                  {row.label}
                </dt>
                <dd className="md:col-span-8 lg:col-span-9 md:pl-6 text-[14.5px] md:text-[15px] text-[#0F1D2D] leading-relaxed">
                  {row.services ? (
                    <ul className="flex flex-wrap gap-x-6 gap-y-2">
                      {row.services.map((s, j) => {
                        const dotColor = ["#5B2E83", "#2F8F3C", "#0E5FB0"][j % 3];
                        return (
                          <li
                            key={s}
                            className="flex items-center gap-2.5 text-[#0F1D2D] font-medium"
                          >
                            <span
                              aria-hidden
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: dotColor }}
                            />
                            {s}
                          </li>
                        );
                      })}
                    </ul>
                  ) : row.link ? (
                    <span className="inline-flex items-center gap-3 flex-wrap">
                      <a
                        href={row.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#0E5FB0] hover:underline font-medium"
                        data-testid={`corporate-profile-${row.key}-link`}
                      >
                        {row.value}
                        <ExternalLink className="h-3 w-3 opacity-70" strokeWidth={2.25} aria-hidden />
                      </a>
                      {row.meta && (
                        <span
                          className="text-[12.5px] font-mono-display tracking-[0.04em] text-slate-500"
                          data-testid={`corporate-profile-${row.key}-meta`}
                        >
                          {row.meta}
                        </span>
                      )}
                    </span>
                  ) : (
                    row.value
                  )}
                </dd>
              </div>
            ))}
          </dl>

          {/* Registry verification footnote — professional, factual */}
          <div
            className="mt-6 rounded-xl bg-white ring-1 ring-[#E2E8F0] px-5 py-4 flex items-start gap-3"
            data-testid="registry-verification-note"
          >
            <ShieldCheck className="h-4 w-4 text-slate-700 mt-0.5 shrink-0" strokeWidth={2} />
            <p className="text-[12.5px] leading-[1.7] text-slate-600">
              {isJa
                ? "上記の法人番号および適格請求書発行事業者登録番号は、国税庁の公開データベースにて検索・照会が可能です。会社情報は2026年5月14日付の履歴事項全部証明書に基づき記載しています。"
                : "The Corporate Number and Invoice Registration Number above can be independently verified via the National Tax Agency's public registries. Corporate facts on this page reflect the certified registry record dated 2026-05-14."}
            </p>
          </div>
        </div>
      </section>

      {/* Registered scope of business — full 21-item legal scope from Articles of Incorporation */}
      <section
        id="registered-scope"
        className="bg-white border-b border-[#E2E8F0]"
        data-testid="registered-scope-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <span className="text-[10px] tracking-[0.28em] font-mono-display text-slate-500 font-bold uppercase">
            {isJa ? "定款記載の事業目的" : "As registered — Articles of Incorporation"}
          </span>
          <h2
            className={[
              "mt-2",
              isJa ? "font-ja" : "font-display",
              "text-[#0F1D2D] text-2xl md:text-3xl tracking-tight font-bold mb-3",
            ].join(" ")}
          >
            {isJa ? "事業目的(登記簿記載)" : "Scope of business (as registered)"}
          </h2>
          <p className="text-[13px] text-slate-500 mb-8 max-w-3xl leading-[1.7]">
            {isJa
              ? "横浜地方法務局横須賀支局に登記された事業目的の全項目です。マーケティング上の表現ではなく、登記上の事業目的として掲載しています。現在実際に運営している事業は「運営サービス」をご覧ください。"
              : "The complete legal scope of business on file at the Yokohama Legal Affairs Bureau (Yokosuka Branch). This is the formal registered scope — not a marketing claim of currently operating services. For services we actively operate today, please refer to the Operated Services section."}
          </p>
          <ol
            className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 list-decimal pl-5"
            data-testid="registered-scope-list"
          >
            {registeredBusinessScope(lang).map((item, i) => (
              <li
                key={i}
                data-testid={`registered-scope-item-${i + 1}`}
                className={[
                  isJa ? "font-ja" : "font-display",
                  "text-[13.5px] md:text-[14px] text-[#0F1D2D] leading-[1.65] marker:text-slate-400 marker:font-mono-display marker:text-[12px]",
                ].join(" ")}
              >
                {item}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Milestones since 2013 */}
      <section
        id="milestones"
        className="bg-white"
        data-testid="corporate-milestones-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <h2
            className={[
              isJa ? "font-ja" : "font-display",
              "text-[#0F1D2D] text-2xl md:text-3xl tracking-tight font-bold mb-2",
            ].join(" ")}
          >
            {isJa ? "2013年からの歩み" : "Milestones since 2013"}
          </h2>
          <p className="text-[13px] text-slate-500 mb-8">
            {isJa
              ? "横須賀で始まった実務サービスから、ETC関連サポート、デジタルインフラへの展開まで。"
              : "From a Yokosuka-based service operation to ETC-related support and digital infrastructure for everyday work in Japan."}
          </p>
          <ol className="relative" data-testid="corporate-milestones">
            <div className="absolute left-3.5 sm:left-[18px] top-0 bottom-0 w-px bg-[#E2E8F0]" />
            {MILESTONES(isJa).map((m, i) => (
              <Reveal as="li" variant="up" delay={i * 35} key={m.period}>
                <div className="relative pl-8 sm:pl-10 pb-6 last:pb-0">
                  <div className="absolute left-0 top-1.5 w-7 h-7 rounded-full ring-4 ring-white bg-white border-2 border-[#0F1D2D] flex items-center justify-center">
                    <span
                      className="h-2 w-2 rounded-full bg-[#5B2E83]"
                      aria-hidden
                    />
                  </div>
                  <div className="rounded-xl bg-[#F8F9FA] ring-1 ring-[#E2E8F0] px-4 py-3">
                    <span className="inline-block text-[10px] tracking-[0.22em] font-mono-display uppercase text-slate-500 font-bold mb-1">
                      {m.period}
                    </span>
                    <p className="text-[14px] text-slate-900 leading-[1.55]">{m.title}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>
      </div>{/* ═════════ END middle content wrapper (aura ends here) ═════════ */}

      {/* Latest & future direction — CTAs */}
      <section className="bg-[#F8F9FA] border-t border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
          <div className="text-center mb-6">
            <span className="text-[10px] tracking-[0.28em] font-mono-display text-slate-500 font-bold uppercase">
              {isJa ? "最新情報と今後の方向性" : "Latest & future direction"}
            </span>
            <h2
              className={[
                "mt-2",
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-xl md:text-2xl tracking-tight font-bold",
              ].join(" ")}
            >
              {isJa
                ? "何が動いていて、これから何が始まるか。"
                : "What's shipped, and what's coming next."}
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to={`/${lang}/whats-new`}
              data-testid="corporate-cta-whats-new"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white ring-1 ring-[#E2E8F0] text-slate-900 text-[13px] font-semibold hover:bg-[#F8F9FA] transition-colors"
            >
              {isJa ? "最新情報" : "What's New"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            <Link
              to={`/${lang}/roadmap`}
              data-testid="corporate-cta-roadmap"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white ring-1 ring-[#E2E8F0] text-slate-900 text-[13px] font-semibold hover:bg-[#F8F9FA] transition-colors"
            >
              {isJa ? "ロードマップ" : "Roadmap"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            <Link
              to={`/${lang}/services`}
              data-testid="corporate-cta-services"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white ring-1 ring-[#E2E8F0] text-slate-900 text-[13px] font-semibold hover:bg-[#F8F9FA] transition-colors"
            >
              {isJa ? "運営サービス" : "Services"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            <Link
              to={`/${lang}/contact`}
              data-testid="corporate-cta-contact"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#5B2E83] text-white text-[13px] font-semibold hover:bg-[#0E5FB0] transition-colors"
            >
              {isJa ? "お問い合わせ" : "Contact us"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
